import { useNavigate, useParams } from 'react-router-dom';
import { useStore } from '../../../utils/store';
import { useEffect, useState, useRef } from 'react';
import HttpService from '../../../services/HttpService';
import Constants from '../../../utils/constants';
import FormFieldData from '../../../Models/FormFieldData';
import { Stack } from '@mui/material';
import CommonForm from '../../../components/form/CommonForm';
import AlertMessageComponent from '../../../components/AlertMessageComponent';
import IdLabelData from '../../../Models/IdLabelData';
import { useAlert } from '../../../components/alertContext';

const CriticalCheckForm: React.FC = () => {
  const isInitialLoad = useRef(true);
  const { getAdmin } = useStore();
  const { currentId } = useParams<{ currentId: any }>();
  const navigate = useNavigate();
  const hpwpItems: IdLabelData[] = [
    { id: 'Hold Point (HP)', label: 'Hold Point (HP)' },
    { id: 'Witness Point (WP)', label: 'Witness Point (WP)' },
  ];
  const { alert, setAlert, handleCloseAlert } = useAlert();
  const [currentRecord, setCurrentRecord] = useState<any>(null);
  type formData = {
    [k: string]: { required: boolean; value: string | null };
  };
  const [form, seForm] = useState<formData>({
    id: { required: false, value: null },
    activity: { required: true, value: '' },
    inspectionPoint: { required: true, value: '' },
    testMethod: { required: true, value: '' },
    acceptanceCriteria: { required: true, value: '' },
    responsibleParty: { required: true, value: '' },
    holdWitnessPoint: { required: true, value: '' },
  });
  const [loading, setLoading] = useState(false);

  const handleRowClick = (record: any) => {
    console.log(record);
    setCurrentRecord(record);
    seForm({
      id: { required: false, value: record.id },
      activity: { required: true, value: record.activity },
      inspectionPoint: { required: true, value: record.inspectionPoint },
      testMethod: { required: true, value: record.testMethod },
      acceptanceCriteria: { required: true, value: record.acceptanceCriteria },
      responsibleParty: { required: true, value: record.responsibleParty },
      holdWitnessPoint: { required: true, value: record.holdWitnessPoint },
    });
  };

  const fetchSingleRecord = async (id: string) => {
    try {
      const response = await HttpService.get(
        `${Constants.apiURL + Constants.criticalcheckURL}/${id}`,
        { headers: { Authorization: `Bearer ${getAdmin()}` } },
        null
      );
      if (response.success) {
        handleRowClick(response.data);
      }
    } catch (error: any) {
      console.error(`PATCH Error:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
    }
  };

  const clearForm = () => {
    seForm({
      id: { required: false, value: null },
      name: { required: true, value: '' },
      location: { required: true, value: '' },
    });
    setAlert({
      type: 'error',
      message: '',
      open: false,
    });
    setCurrentRecord(null);
    navigate('/data-management/critical-checks');
  };

  const handleSave = async () => {
    setLoading(true);
    const reqData: any = {
      holdWitnessPoint: form.holdWitnessPoint.value,
      responsibleParty: form.responsibleParty.value,
      acceptanceCriteria: form.acceptanceCriteria.value,
      testMethod: form.testMethod.value,
      inspectionPoint: form.inspectionPoint.value,
      activity: form.activity.value,
    };
    const endpoint = `${Constants.apiURL + Constants.criticalcheckURL}`;
    let response: any;
    try {
      if (currentRecord) {
        reqData['id'] = form.id.value;
      }
      response = await HttpService.post(endpoint, reqData, {
        headers: { Authorization: `Bearer ${getAdmin()}` },
      });

      if (response.success) {
        clearForm();
      }
    } catch (error: any) {
      console.error(`PATCH Error:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    seForm((prevData) => ({
      ...prevData,
      [name]: { ...prevData[name], value: value },
    }));
  };

  useEffect(() => {
    if (currentId) {
      if (currentId !== 'Add' && isInitialLoad.current === true) {
        isInitialLoad.current = false;
        fetchSingleRecord(currentId);
      }
    } else {
      setCurrentRecord(null);
    }
  }, [currentId]);

  const formFields: FormFieldData[] = [
    {
      name: 'activity',
      label: 'Activity',
      value: form.activity.value,
      onChange: handleInputChange,
      isRequired: form.activity.required,
      size: 6,
      type: 'textfield',
      validationRules: [(value) => (!value ? 'Activity is required' : null)],
    },
    {
      name: 'inspectionPoint',
      label: 'Inspection point',
      value: form.inspectionPoint.value,
      onChange: handleInputChange,
      isRequired: form.inspectionPoint.required,
      size: 6,
      type: 'textfield',
      validationRules: [
        (value) => (!value ? 'Inspection poin is required' : null),
      ],
    },
    {
      name: 'testMethod',
      label: 'Test Method',
      value: form.testMethod.value,
      onChange: handleInputChange,
      isRequired: form.testMethod.required,
      size: 6,
      type: 'textfield',
      validationRules: [(value) => (!value ? 'Test Method is required' : null)],
    },
    {
      name: 'acceptanceCriteria',
      label: 'Acceptance Criteria',
      value: form.acceptanceCriteria.value,
      onChange: handleInputChange,
      isRequired: form.acceptanceCriteria.required,
      size: 6,
      type: 'textfield',
      validationRules: [
        (value) => (!value ? 'Acceptance Criteria is required' : null),
      ],
    },
    {
      name: 'responsibleParty',
      label: 'Responsible Party',
      value: form.responsibleParty.value,
      onChange: handleInputChange,
      isRequired: form.responsibleParty.required,
      size: 6,
      type: 'textfield',
      validationRules: [
        (value) => (!value ? 'Responsible Party is required' : null),
      ],
    },
    {
      name: 'holdWitnessPoint',
      label: 'Hold Point (HP) Witness Point (WP)',
      value: form.holdWitnessPoint.value,
      onChange: handleInputChange,
      isRequired: form.holdWitnessPoint.required,
      size: 6,
      options: hpwpItems,
      type: 'selectField',
      validationRules: [
        (value) =>
          !value ? 'Hold Point (HP) Witness Point (WP) is required' : null,
      ],
    },
  ];
  return (
    <Stack>
      <CommonForm
        fields={formFields}
        onCancel={() => {}}
        formReset={clearForm}
        formTitle={
          currentId !== 'Add' ? 'Update Critical Check' : 'Add Critical Check'
        }
        isLoading={loading}
        isUpdate={currentId !== 'Add' ? true : false}
        onSave={handleSave}
        actionButtonPosition="bottom"
      />
      <AlertMessageComponent
        type={alert.type}
        open={alert.open}
        handleClose={handleCloseAlert}
        message={alert.message}
      />
    </Stack>
  );
};

export default CriticalCheckForm;
